import { onUnmounted, ref } from 'vue';
import type {
  AmlReview,
  AmlReviewAttachment,
  AmlReviewAttachmentType,
} from '@/integrations/Immersve/aml-review/types';
import type { FrankieOneScannedDocument } from '@/integrations/Immersve/FrankieOne/types';
import type { ImmersveApiClient } from '@/hooks/useImmersveApiClient';

export type CaseDocumentState = 'loading' | 'ready' | 'failed';

export interface CaseDocument {
  fileId: string;
  type: AmlReviewAttachmentType;
  label: string;
  mimeType?: string;
  state: CaseDocumentState;
  objectUrl?: string;
  reason?: string;
  // Per-document rotation in degrees, normalized to [0, 360). Lives here
  // rather than in the viewer so it survives closing and reopening the
  // preview (matches the v5 design's app-level rotation state).
  rotation: number;
}

const DOCUMENT_LABELS: Record<AmlReviewAttachmentType, string> = {
  'self-image': 'Selfie',
  passport: 'Passport',
  'national-id': 'National ID',
  'drivers-license': "Driver's licence",
  'residence-permit': 'Residence permit',
  'proof-of-address': 'Proof of address',
  'data-source': 'Data source',
  other: 'Document',
};

export function documentLabel(type: AmlReviewAttachmentType): string {
  return DOCUMENT_LABELS[type] ?? 'Document';
}

export function useCaseDocuments() {
  const documents = ref<CaseDocument[]>([]);
  const unavailableReason = ref<string | null>(null);

  function rotateDocument(fileId: string, delta: number) {
    const target = documents.value.find((doc) => doc.fileId === fileId);
    if (!target) return;
    target.rotation = (((target.rotation + delta) % 360) + 360) % 360;
  }

  function load(client: ImmersveApiClient, review: AmlReview) {
    documents.value = [];
    unavailableReason.value = null;

    const attachments = review.attachments ?? [];

    if (attachments.length === 0) {
      return;
    }

    documents.value = attachments.map((attachment) => toInitialDocument(attachment));

    if (review.kycProvider === 'frankieOne') {
      fetchFrankieOneDocuments(client, review.amlReviewId, attachments);
      return;
    }

    for (const attachment of attachments) {
      if (attachment.location !== 'provider-proxy') continue;
      void fetchOne(client, review.amlReviewId, attachment);
    }
  }

  async function fetchOne(
    client: ImmersveApiClient,
    amlReviewId: string,
    attachment: AmlReviewAttachment
  ) {
    try {
      const { blob, mimeType } = await client.getAmlReviewAttachment(
        amlReviewId,
        attachment.fileId
      );
      resolveDocument(attachment.fileId, blob, mimeType);
    } catch (err) {
      failDocument(attachment.fileId, (err as Error).message);
    }
  }

  function fetchFrankieOneDocuments(
    client: ImmersveApiClient,
    amlReviewId: string,
    attachments: AmlReviewAttachment[]
  ) {
    const groups = new Map<string, { fileId: string; attachments: AmlReviewAttachment[] }>();

    for (const attachment of attachments) {
      if (attachment.location !== 'provider-proxy') continue;

      const reference = parseFrankieOneReference(attachment.providerFileReference);
      if (!reference) {
        failDocument(attachment.fileId, 'Could not determine which document this scan belongs to.');
        continue;
      }

      const group = groups.get(reference.documentId);
      if (group) {
        group.attachments.push(attachment);
      } else {
        groups.set(reference.documentId, { fileId: attachment.fileId, attachments: [attachment] });
      }
    }

    for (const group of groups.values()) {
      void fetchFrankieOneDocument(client, amlReviewId, group.fileId, group.attachments);
    }
  }

  async function fetchFrankieOneDocument(
    client: ImmersveApiClient,
    amlReviewId: string,
    fileId: string,
    attachments: AmlReviewAttachment[]
  ) {
    let scans: FrankieOneScannedDocument[];
    try {
      const response = await client.getAmlReviewAttachmentJson(amlReviewId, fileId);
      scans = response.document?.docScan ?? [];
    } catch (err) {
      const message = (err as Error).message;
      for (const attachment of attachments) {
        failDocument(attachment.fileId, message);
      }
      return;
    }

    for (const attachment of attachments) {
      const reference = parseFrankieOneReference(attachment.providerFileReference);
      const scan = reference && scans.find((s) => s.scanDocId === reference.scanDocId);

      if (!scan) {
        failDocument(attachment.fileId, 'Scan not found in the provider document.');
        continue;
      }
      if (!scan.scanData) {
        failDocument(attachment.fileId, 'This scan is not available from the provider.');
        continue;
      }

      const mimeType = scan.scanMIME ?? attachment.mimeType ?? 'application/octet-stream';
      resolveDocument(attachment.fileId, base64ToBlob(scan.scanData, mimeType), mimeType);
    }
  }

  function resolveDocument(fileId: string, blob: Blob, mimeType?: string) {
    const target = documents.value.find((doc) => doc.fileId === fileId);
    if (!target) return;
    target.mimeType = mimeType ?? target.mimeType;
    target.objectUrl = URL.createObjectURL(blob);
    target.state = 'ready';
  }

  function failDocument(fileId: string, reason: string) {
    const target = documents.value.find((doc) => doc.fileId === fileId);
    if (!target) return;
    target.state = 'failed';
    target.reason = reason;
  }

  onUnmounted(() => {
    for (const doc of documents.value) {
      if (doc.objectUrl) {
        URL.revokeObjectURL(doc.objectUrl);
      }
    }
  });

  return { documents, unavailableReason, load, rotateDocument };
}

function toInitialDocument(attachment: AmlReviewAttachment): CaseDocument {
  if (attachment.location !== 'provider-proxy') {
    return {
      fileId: attachment.fileId,
      type: attachment.type,
      label: documentLabel(attachment.type),
      mimeType: attachment.mimeType,
      state: 'failed',
      reason: 'Not retrievable from this location yet.',
      rotation: 0,
    };
  }
  return {
    fileId: attachment.fileId,
    type: attachment.type,
    label: documentLabel(attachment.type),
    mimeType: attachment.mimeType,
    state: 'loading',
    rotation: 0,
  };
}

interface FrankieOneReference {
  documentId: string;
  scanDocId: string;
}

// FrankieOneDocumentResolver (amethyst) writes providerFileReference as
// `${entityId}:${documentId}:${scanDocId}`. entityId isn't needed here —
// it's the same for every attachment on a review.
function parseFrankieOneReference(providerFileReference: string): FrankieOneReference | null {
  const parts = providerFileReference.split(':');
  if (parts.length !== 3 || parts.some((part) => !part)) return null;
  const [, documentId, scanDocId] = parts;
  return { documentId, scanDocId };
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}
